import React, { useState } from 'react';
import { UploadButton } from '@go-client/components/UIkit/forms/UploadButton/UploadButton';
import { InlineIcon } from '@spark-labs/avocado-ui';
import { UPLOAD_RULES } from '@go-shared/lib/uploads';

const TEMPLATE_IMAGE_LIMIT = 4;

type WithImage = { images: string[] };

interface TemplateFormImagesProps {
  imageLimit?: number;
  template: WithImage;
  onImageUploaded: (file: File) => void;
  onImageRemoved: (imageIndex: number) => void;
  uploadQueue?: File[];
}

const readerLoadEnd = async (file: File): Promise<string> => {
  return new Promise((resolve, _reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.readAsDataURL(file);
  });
};

export const TemplateFormImages: React.FC<TemplateFormImagesProps> = ({
  imageLimit,
  template: eventTemplate,
  onImageUploaded,
  onImageRemoved,
  uploadQueue,
}) => {
  const [imagesBase64, setImagesBase64] = useState<string[]>([]);

  React.useEffect(() => {
    if (!uploadQueue?.length) {
      return;
    }

    Promise.all(uploadQueue.map(file => readerLoadEnd(file)))
      .then(images => setImagesBase64(images))
      .catch(error => console.error(error));
  }, [uploadQueue]);

  const handleFilesChange = async (files: FileList) => {
    if (files.length) {
      onImageUploaded(files[0]);
    }
  };

  const renderUploadImageBox = () => {
    return (
      <UploadButton
        onFilesChange={handleFilesChange}
        uploadRule={UPLOAD_RULES.venueImage}
        render={upload => (
          <div
            onClick={upload}
            className="cursor-pointer w-20 h-20 rounded bg-grey-lighter flex items-center
                        justify-center border-dashed border-2 border-grey-dark mb-4"
          >
            <InlineIcon name="plus" className="text-grey-dark text-base" />
          </div>
        )}
      />
    );
  };

  const renderImage = (location: string, index: number) => {
    return (
      <div
        key={index}
        className="w-20 h-20 rounded bg-grey-light flex items-center
                      justify-center mr-4 mb-4 relative"
      >
        <div
          onClick={() => onImageRemoved(index)}
          className="bg-white h-5 w-5 top-0 right-0 mr-1 mt-1 absolute cursor-pointer
                        rounded-full icon-small items-center flex justify-center"
        >
          <InlineIcon name="close" className="text-xxs text-grey-dark" />
        </div>
        <img src={location} className="object-contain w-full h-full" />
      </div>
    );
  };

  const finalImageLimit = imageLimit !== undefined ? imageLimit : TEMPLATE_IMAGE_LIMIT;

  const imagesToUse =
    imagesBase64.length > 0 ? imagesBase64.map(renderImage) : eventTemplate.images.map(renderImage);

  return (
    <div className="flex flex-row flex-wrap">
      {imagesToUse}
      {imagesToUse.length < finalImageLimit && renderUploadImageBox()}
    </div>
  );
};
